import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import { SmartcardReader } from '../utils/smartcardReader';
import { SmartcardReaderHID } from '../utils/smartcardReaderHID';
import { io, Socket } from 'socket.io-client';

interface SmartcardContextType {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  lastScannedCode: string | null;
  connect: () => Promise<boolean>;
  disconnect: () => Promise<void>;
  onScan: (callback: (code: string) => void) => void;
  offScan: (callback: (code: string) => void) => void;
}

const SmartcardContext = createContext<SmartcardContextType | null>(null);

export const SmartcardProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastScannedCode, setLastScannedCode] = useState<string | null>(null);
  const [reader, setReader] = useState<SmartcardReader | null>(null);
  const [hidReader, setHidReader] = useState<SmartcardReaderHID | null>(null);
  const [socket, setSocket] = useState<Socket | WebSocket | null>(null);
  const [scanCallbacks, setScanCallbacks] = useState<Set<(code: string) => void>>(new Set());
  const [connectionMode, setConnectionMode] = useState<'usb' | 'webhid' | 'websocket' | 'local-service' | null>(null);

  const connect = useCallback(async (): Promise<boolean> => {
    if (isConnected || isConnecting) return isConnected;
    
    setIsConnecting(true);
    setError(null);
    
    // PRIORITY 1: Try WebHID (works on Windows with NFC readers in HID mode)
    console.log('[SmartcardContext] Attempting WebHID connection (Keyboard Emulation)...');
    const hidConnected = await attemptWebHIDConnection();
    
    if (hidConnected) {
      setConnectionMode('webhid');
      setIsConnecting(false);
      return true;
    }

    // PRIORITY 2: Try WebUSB (works on Linux, blocked on Windows)
    console.log('[SmartcardContext] WebHID failed, trying WebUSB...');
    const usbConnected = await attemptWebUSBConnection();
    
    if (usbConnected) {
      setConnectionMode('usb');
      setIsConnecting(false);
      return true;
    }

    // PRIORITY 3: Fallback to WebSocket (simulator or backend)
    console.log('[SmartcardContext] Direct access failed, trying WebSocket fallback...');
    const wsConnected = await attemptWebSocketConnection();
    
    if (wsConnected) {
      setConnectionMode('websocket');
      setIsConnecting(false);
      return true;
    }

    setIsConnecting(false);
    return false;
  }, [isConnected, isConnecting, scanCallbacks]);

  const attemptWebUSBConnection = async (): Promise<boolean> => {
    try {
      const newReader = new SmartcardReader();
      const connected = await newReader.connect({
        onData: (data: string) => {
          console.log('[SmartcardContext] USB Card data received:', data);
          setLastScannedCode(data);
          scanCallbacks.forEach(cb => cb(data));
        },
        onError: (err: string) => {
          console.error('[SmartcardContext] USB Error:', err);
          // Only show error if we're already connected, or keep it as fallback trigger
          if (isConnected) {
            setError(`Lesefehler: ${err}`);
          }
        },
        onConnected: () => {
          console.log('[SmartcardContext] ✅ Connected to USB smartcard reader');
          setIsConnected(true);
          setError(null);
        },
        onDisconnected: () => {
          console.log('[SmartcardContext] 📵 Disconnected from USB smartcard reader');
          setIsConnected(false);
          setReader(null);
        }
      });
      
      if (connected) {
        setReader(newReader);
        setIsConnected(true);
        return true;
      }
      return false;
    } catch (e: any) {
      console.log('[SmartcardContext] WebUSB fallback triggered:', e?.message);
      return false;
    }
  };

  const attemptWebHIDConnection = async (): Promise<boolean> => {
    try {
      if (!('hid' in navigator)) {
        console.log('[SmartcardContext] WebHID nicht verfügbar in diesem Browser');
        return false;
      }

      const newHidReader = new SmartcardReaderHID({
        onScan: (code: string) => {
          console.log('[SmartcardContext] 🎫 Karte gescannt (WebHID):', code);
          setLastScannedCode(code);
          scanCallbacks.forEach(cb => cb(code));
        },
        onError: (err: string) => {
          console.error('[SmartcardContext] WebHID Error:', err);
          if (isConnected) {
            setError(`WebHID Fehler: ${err}`);
          }
        },
        onConnected: () => {
          console.log('[SmartcardContext] ✅ Connected via WebHID (Keyboard Emulation Mode)');
          setIsConnected(true);
          setError(null);
        },
        onDisconnected: () => {
          console.log('[SmartcardContext] 📵 Disconnected from WebHID reader');
          setIsConnected(false);
          setHidReader(null);
        }
      });

      const connected = await newHidReader.connect();
      
      if (connected) {
        setHidReader(newHidReader);
        setIsConnected(true);
        
        const deviceInfo = newHidReader.getDeviceInfo();
        if (deviceInfo) {
          console.log('[SmartcardContext] WebHID Device:', deviceInfo);
        }
        
        return true;
      }
      return false;

    } catch (e: any) {
      console.log('[SmartcardContext] WebHID nicht verfügbar:', e?.message);
      return false;
    }
  };

  const attemptLocalServiceConnection = async (): Promise<boolean> => {
    return new Promise((resolve) => {
      console.log('[SmartcardContext] Versuche Verbindung zum lokalen Windows Service...');
      
      const localWs = new WebSocket('ws://localhost:8765');
      
      const timeout = setTimeout(() => {
        console.log('[SmartcardContext] Timeout: Kein lokaler Service gefunden');
        localWs.close();
        resolve(false);
      }, 2000); // 2 Sekunden Timeout
      
      localWs.onopen = () => {
        clearTimeout(timeout);
        console.log('[SmartcardContext] ✅ Verbunden mit lokalem Windows Service (echte Karten)');
        setIsConnected(true);
        setError(null);
        setSocket(localWs);
        
        // Service starten
        localWs.send(JSON.stringify({ type: 'start' }));
        
        // Nachrichten empfangen
        localWs.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            if (data.type === 'read') {
              console.log('[SmartcardContext] 🎫 Echte Karte gelesen:', data.code);
              setLastScannedCode(data.code);
              scanCallbacks.forEach(cb => cb(data.code));
            } else if (data.type === 'connected') {
              console.log('[SmartcardContext] Service bereit:', data.readers);
            } else if (data.type === 'removed') {
              console.log('[SmartcardContext] 📭 Karte entfernt');
            }
          } catch (err) {
            console.error('[SmartcardContext] Fehler beim Parsen:', err);
          }
        };
        
        localWs.onerror = (err) => {
          console.error('[SmartcardContext] Lokaler Service Fehler:', err);
          setIsConnected(false);
        };
        
        localWs.onclose = () => {
          console.log('[SmartcardContext] Lokaler Service getrennt');
          setIsConnected(false);
        };
        
        resolve(true);
      };
      
      localWs.onerror = () => {
        clearTimeout(timeout);
        console.log('[SmartcardContext] Kein lokaler Service verfügbar');
        resolve(false);
      };
    });
  };

  const attemptWebSocketConnection = async (): Promise<boolean> => {
    // Versuche zuerst lokalen Windows Service (Port 8765)
    const localServiceConnected = await attemptLocalServiceConnection();
    if (localServiceConnected) {
      setConnectionMode('local-service');
      return true;
    }

    // Fallback: Backend Gateway (Simulator)
    try {
      // Use window location for production (nginx proxy), or direct backend for dev
      const socketUrl = window.location.hostname === 'localhost' 
        ? `http://${window.location.hostname}:3000`
        : window.location.origin;
      
      console.log('[SmartcardContext] Connecting to backend WebSocket:', socketUrl);
      
      const newSocket = io(`${socketUrl}/smartcard`, {
        path: '/socket.io/',
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5
      });
      
      return new Promise<boolean>((resolve) => {
        const onConnect = () => {
          console.log('[SmartcardContext] ✅ Connected to WebSocket gateway (simulator mode)');
          setIsConnected(true);
          setError(null);
          newSocket.off('connect', onConnect);
          newSocket.emit('start');
          
          // Listen for card read events
          newSocket.on('read', (data: { code: string; timestamp: string }) => {
            console.log('[SmartcardContext] 🎫 Card read (WebSocket):', data.code);
            setLastScannedCode(data.code);
            scanCallbacks.forEach(cb => cb(data.code));
          });
          
          resolve(true);
        };
        
        const onError = (err: any) => {
          console.error('[SmartcardContext] WebSocket Error:', err);
          setError(`WebSocket Fehler: ${err?.message || 'Unbekannt'}`);
          newSocket.off('error', onError);
          resolve(false);
        };
        
        const onDisconnect = () => {
          console.log('[SmartcardContext] Disconnected from WebSocket');
          setIsConnected(false);
        };
        
        newSocket.on('connect', onConnect);
        newSocket.on('error', onError);
        newSocket.on('disconnect', onDisconnect);
        
        setSocket(newSocket);
        
        setTimeout(() => {
          if (!newSocket.connected) {
            newSocket.disconnect();
            setError('WebSocket Verbindungszeitüberschreitung');
            resolve(false);
          }
        }, 5000);
      });
    } catch (e: any) {
      console.error('[SmartcardContext] WebSocket connection error:', e);
      setError(e?.message || 'WebSocket Fehler');
      return false;
    }
  };

  const disconnect = useCallback(async (): Promise<void> => {
    if (hidReader && connectionMode === 'webhid') {
      console.log('[SmartcardContext] Disconnecting from WebHID reader');
      await hidReader.disconnect();
      setHidReader(null);
    }
    
    if (reader && connectionMode === 'usb') {
      console.log('[SmartcardContext] Disconnecting from USB reader');
      await reader.disconnect();
      setReader(null);
    }
    
    if (socket && (connectionMode === 'websocket' || connectionMode === 'local-service')) {
      console.log('[SmartcardContext] Disconnecting from WebSocket');
      if ('disconnect' in socket) {
        socket.disconnect();
      } else {
        socket.close();
      }
      setSocket(null);
    }
    
    setIsConnected(false);
    setError(null);
    setConnectionMode(null);
  }, [reader, socket, connectionMode]);

  const onScan = useCallback((callback: (code: string) => void) => {
    setScanCallbacks(prev => new Set(prev).add(callback));
  }, []);

  const offScan = useCallback((callback: (code: string) => void) => {
    setScanCallbacks(prev => {
      const newSet = new Set(prev);
      newSet.delete(callback);
      return newSet;
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reader) {
        reader.disconnect().catch(() => {});
      }
      if (socket) {
        socket.disconnect();
      }
    };
  }, [reader, socket]);

  const value: SmartcardContextType = {
    isConnected,
    isConnecting,
    error,
    lastScannedCode,
    connect,
    disconnect,
    onScan,
    offScan
  };

  return (
    <SmartcardContext.Provider value={value}>
      {children}
    </SmartcardContext.Provider>
  );
};

export const useSmartcard = (): SmartcardContextType => {
  const context = useContext(SmartcardContext);
  if (!context) {
    throw new Error('useSmartcard must be used within SmartcardProvider');
  }
  return context;
};
